import { type FormEvent, useState } from 'react';
import { useChangeEmail, useMe, useMfaDisable, useMfaEnable, useMfaSetup } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Alert, Button, Card, Field, PasswordField } from '../components/ui.js';
import { ChangePasswordForm } from './ChangePasswordPage.js';

const codeError = (error: unknown): string | null =>
  error instanceof ApiError && error.code === 'invalid_code'
    ? 'That code is not valid. Try the current one from your app.'
    : error
      ? 'Something went wrong. Please try again.'
      : null;

/** Account settings for any signed-in user: email, password, and two-factor. */
export function SettingsPage() {
  const { data: user } = useMe();
  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div>
        <h1 className="font-title text-3xl tracking-wide">Settings</h1>
        <p className="opacity-70">Manage your account and sign-in security.</p>
      </div>
      <EmailSection currentEmail={user?.email ?? ''} />
      <Card>
        <h2 className="mb-3 font-title text-xl tracking-wide">Password</h2>
        <ChangePasswordForm />
      </Card>
      <MfaSection enabled={user?.mfaEnabled ?? false} />
    </div>
  );
}

function EmailSection({ currentEmail }: { currentEmail: string }) {
  const change = useChangeEmail();
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    change.mutate({ newEmail, password }, { onSuccess: () => setPassword('') });
  };

  const error =
    change.error instanceof ApiError && change.error.code === 'email_in_use'
      ? 'That email is already in use.'
      : change.error instanceof ApiError && change.error.code === 'wrong_password'
        ? 'Your password is incorrect.'
        : change.isError
          ? 'Something went wrong. Please try again.'
          : null;

  return (
    <Card>
      <h2 className="mb-1 font-title text-xl tracking-wide">Email</h2>
      <p className="mb-3 text-sm opacity-70">Current: {currentEmail}</p>
      <form className="flex flex-col gap-3" onSubmit={onSubmit}>
        {error ? <Alert>{error}</Alert> : null}
        {change.isSuccess ? <Alert kind="info">Your email has been updated.</Alert> : null}
        <Field
          label="New email"
          type="email"
          autoComplete="email"
          required
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
        />
        <PasswordField
          label="Confirm with your password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div>
          <Button type="submit" disabled={change.isPending || !newEmail || !password}>
            {change.isPending ? 'Saving…' : 'Update email'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function MfaSection({ enabled }: { enabled: boolean }) {
  const setup = useMfaSetup();
  const enable = useMfaEnable();
  const disable = useMfaDisable();
  const [code, setCode] = useState('');
  const onCode = (value: string): void => setCode(value.replace(/\D/g, '').slice(0, 8));

  return (
    <Card>
      <h2 className="mb-1 font-title text-xl tracking-wide">Two-factor authentication</h2>
      {enabled ? (
        <>
          <div className="mb-3 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" />
            <span className="text-sm font-medium">On.</span>
            <span className="text-sm opacity-70">Enter a current code to turn it off.</span>
          </div>
          {disable.isError ? (
            <div className="mb-3">
              <Alert>{codeError(disable.error)}</Alert>
            </div>
          ) : null}
          <div className="flex items-end gap-3">
            <div className="w-40">
              <Field
                label="Authentication code"
                inputMode="numeric"
                placeholder="123456"
                value={code}
                onChange={(e) => onCode(e.target.value)}
              />
            </div>
            <Button
              variant="danger"
              disabled={disable.isPending || code.length < 6}
              onClick={() => disable.mutate(code)}
            >
              {disable.isPending ? 'Disabling…' : 'Disable'}
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className="mb-3 text-sm opacity-70">
            Protect sign-in with a time-based one-time code (Google Authenticator, 1Password,
            Authy…).
          </p>
          {!setup.data ? (
            <Button disabled={setup.isPending} onClick={() => setup.mutate()}>
              {setup.isPending ? 'Preparing…' : 'Begin setup'}
            </Button>
          ) : (
            <div className="flex flex-col gap-4">
              <ol className="list-decimal space-y-3 pl-5 text-sm">
                <li>
                  Add this account to your authenticator — scan the URI, or enter the secret by
                  hand:
                  <div className="mt-2 space-y-2">
                    <code className="block overflow-x-auto rounded bg-neutral/70 p-2 text-xs">
                      {setup.data.otpauthUrl}
                    </code>
                    <div className="text-xs">
                      Secret: <span className="font-mono tracking-widest">{setup.data.secret}</span>
                    </div>
                  </div>
                </li>
                <li>Enter the 6-digit code it shows to confirm:</li>
              </ol>
              {enable.isError ? <Alert>{codeError(enable.error)}</Alert> : null}
              <div className="flex items-end gap-3">
                <div className="w-40">
                  <Field
                    label="Authentication code"
                    inputMode="numeric"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => onCode(e.target.value)}
                  />
                </div>
                <Button
                  disabled={enable.isPending || code.length < 6}
                  onClick={() => enable.mutate(code)}
                >
                  {enable.isPending ? 'Enabling…' : 'Enable'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
